import { useMemo } from 'react';

/**
 * Renders the right input type for a document-template variable
 * based on heuristics from its name. The backend stores template
 * variables as flat string arrays (no per-var schema), so we infer:
 *
 *   *_date / date_*            → <input type="date">
 *   *_email                    → <input type="email">
 *   *_url, *_website           → <input type="url">
 *   *_salary, *_amount, *_usd, *_jpy, *_eur, *_gbp, *_krw, *_cny,
 *   *_months, *_years, *_days, *_count, *_pct, probation_*    → number
 *   pay_frequency              → <select> with payroll cycle options
 *   employment_type            → <select> with FTE/contractor/etc
 *   *_summary, *_description,
 *   *_notes, *_terms, *_clause → <textarea>
 *   otherwise                  → <input type="text">
 *
 * The label is humanized: "base_salary_usd" → "Base salary (USD)".
 */
interface Props {
  name: string;
  value: string;
  onChange: (v: string) => void;
}

const PAY_FREQUENCIES = [
  { value: 'monthly',     label: 'Monthly' },
  { value: 'semi-monthly',label: 'Semi-monthly (1st + 15th)' },
  { value: 'bi-weekly',   label: 'Bi-weekly (every 2 weeks)' },
  { value: 'weekly',      label: 'Weekly' },
  { value: 'annually',    label: 'Annually' },
];

const EMPLOYMENT_TYPES = [
  { value: 'full-time',   label: 'Full-time' },
  { value: 'part-time',   label: 'Part-time' },
  { value: 'contractor',  label: 'Contractor' },
  { value: 'intern',      label: 'Intern' },
  { value: '正社員 / seishain',     label: '正社員 (Seishain)' },
  { value: '契約社員 / keiyaku',     label: '契約社員 (Keiyaku)' },
  { value: '業務委託 / gyoumu_itaku',label: '業務委託 (Gyoumu itaku)' },
];

const CURRENCY_SUFFIXES = ['usd', 'jpy', 'eur', 'gbp', 'krw', 'cny', 'cad', 'aud', 'sgd', 'hkd'] as const;

type Kind = 'text' | 'date' | 'number' | 'email' | 'url' | 'textarea' | 'select-payfreq' | 'select-employment';

function kindFor(name: string): Kind {
  const n = name.toLowerCase();
  if (n === 'pay_frequency') return 'select-payfreq';
  if (n === 'employment_type') return 'select-employment';
  if (n.endsWith('_date') || n.startsWith('date_') || n === 'date') return 'date';
  if (n.endsWith('_email') || n === 'email') return 'email';
  if (n.endsWith('_url') || n.endsWith('_website') || n === 'url') return 'url';
  if (n.endsWith('_summary') || n.endsWith('_description') || n.endsWith('_notes') || n.endsWith('_terms') || n.endsWith('_clause')) return 'textarea';
  if (
    n.endsWith('_salary') || n.endsWith('_amount') || n.endsWith('_cost') || n.endsWith('_price') ||
    n.endsWith('_cents') || CURRENCY_SUFFIXES.some((c) => n.endsWith('_' + c)) ||
    n.endsWith('_months') || n.endsWith('_years') || n.endsWith('_days') ||
    n.endsWith('_count') || n.endsWith('_pct') || n.endsWith('_percent') || n.endsWith('_number') ||
    n.startsWith('probation_')
  ) return 'number';
  return 'text';
}

function humanLabel(name: string): string {
  // base_salary_usd → "Base salary (USD)"
  const currency = CURRENCY_SUFFIXES.find((c) => name.toLowerCase().endsWith('_' + c));
  let body = currency ? name.slice(0, -currency.length - 1) : name;
  body = body.replace(/_/g, ' ').trim();
  body = body.charAt(0).toUpperCase() + body.slice(1);
  return currency ? `${body} (${currency.toUpperCase()})` : body;
}

export function VariableField({ name, value, onChange }: Props) {
  const kind = useMemo(() => kindFor(name), [name]);
  const label = useMemo(() => humanLabel(name), [name]);

  const labelEl = (
    <span className="block text-xs font-medium text-ink-700 mb-1">
      {label}
      <span className="ms-2 text-[10px] text-ink-400 font-mono font-normal">{name}</span>
    </span>
  );

  if (kind === 'textarea') {
    return (
      <label className="block sm:col-span-2">
        {labelEl}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="doc-var-input"
        />
      </label>
    );
  }

  if (kind === 'select-payfreq') {
    return (
      <label className="block">
        {labelEl}
        <select value={value} onChange={(e) => onChange(e.target.value)} className="doc-var-input">
          <option value="">—</option>
          {PAY_FREQUENCIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }

  if (kind === 'select-employment') {
    return (
      <label className="block">
        {labelEl}
        <select value={value} onChange={(e) => onChange(e.target.value)} className="doc-var-input">
          <option value="">—</option>
          {EMPLOYMENT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }

  // Map kind → HTML input type. number stays a string upstream so we
  // don't fight the bindings shape (Record<string, string>) — it just
  // gets a numeric keypad on mobile + arrow steppers on desktop.
  const type =
    kind === 'date'    ? 'date'    :
    kind === 'number'  ? 'number'  :
    kind === 'email'   ? 'email'   :
    kind === 'url'     ? 'url'     :
    'text';

  return (
    <label className="block">
      {labelEl}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="doc-var-input"
        // Steppers/min for number fields. Salary fields can't be negative;
        // currency-suffix vars use 100 unit steps (¥ amounts), others 1.
        min={kind === 'number' ? 0 : undefined}
        step={
          kind === 'number'
            ? (name.toLowerCase().endsWith('_months') || name.toLowerCase().endsWith('_years') || name.toLowerCase().endsWith('_days') || name.toLowerCase().endsWith('_count') ? 1 : 100)
            : undefined
        }
      />
    </label>
  );
}
